import {Injectable, OnDestroy} from '@angular/core';
import {BaseViewModel} from '../../../../../models/base/base-view-model';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, tap} from 'rxjs/operators';
import {GuidesDomainModel} from '../../../../../domain-models/guides-domain-model';
import {GuideClickType} from '../../../../../models/guide/enum/guide-click-type.enum';
import {ChartDataAs} from '../models/chart-data-as.enum';
import {ChartData} from '../models/chart-data';
import {KrugoDateRange} from '../krugo-date-picker/models/krugo-date-range';
import {DistinctUtils} from '../../../../../utils/distinct.utils';
import {InsightsDomainModel} from '../../../../../domain-models/insights-domain-model';
import {DateUtils} from '../../../../../utils/date-utils';
import {GuideInsightReq} from '../../../../../models/guide/requests/guide-insight-req';
import {LoadingOptions} from '../../../../../models/shared/loading-options';
import {GuideInsight} from '../../../../../models/guide/dto/guide-insight';

@Injectable()
export class GuideInsightChartViewModel extends BaseViewModel implements OnDestroy {

  // Popper
  public popperModifier = {
    flip: {
      behavior: ['right', 'bottom', 'top']
    }
  };
  public popperStyles = {
    'background-color': '#FFFFFF',
    padding: 0
  };

  // Loading Spinner
  public loadingOpts: LoadingOptions = LoadingOptions.default();

  // Selected Insight Type
  private selectedClickType = new BehaviorSubject<GuideClickType>(GuideClickType.Guide);
  public selectedClickType$ = this.selectedClickType.asObservable();

  // Date range
  private selectedDateRange = new BehaviorSubject<KrugoDateRange>(KrugoDateRange.last30Days());
  public dateRangeString$ = this.selectedDateRange.notNull()
    .pipe(
      distinctUntilChanged(DistinctUtils.distinctKrugoDateRange),
      map(dates => dates.getDateRangeString()),
    );

  // Chart Data
  private guideId = new BehaviorSubject<string>(null);
  private dataAs = new BehaviorSubject<ChartDataAs>(ChartDataAs.GuideViews);
  /** plural and returns list because chart can technically plot multiple insights on one graph - future proofing. */
  private guideInsights = combineLatest([
    this.guideId.notNull(),
    this.selectedDateRange.notNull().pipe(distinctUntilChanged(DistinctUtils.distinctKrugoDateRange)),
    this.insightsDomainModel.guideInsights$
  ]).pipe(
      debounceTime(1),
      map(([guideId, dates, insights]) => {
        const key = DateUtils.buildDateKey(guideId, dates.getDateRangeString());
        if (!!insights) {
          const insight = insights.get(key);
          if (!!insight) {
            return [insight];
          } else {
            const getDataReq = new GuideInsightReq(guideId, dates.getDateRangeString(), dates.startDate, dates.endDate, false);
            this.insightsDomainModel.addGuideInsightReq(getDataReq);
          }
        }
        return null;
      })
    );
  public insightsLoaded$ = this.guideInsights.pipe(map(insights => !!insights));
  /** Determines what cards are shown above chart (guide views, social media view, etc) */
  public clickMap$ = this.guideInsights.pipe(map(insights => {
    if (!!insights && insights.length > 0) {
      const value = insights[0]?.totalClickMap as Map<GuideClickType, number>;
      if (value) { return value; } else { return GuideInsight.emptyMap(); }
    } else {
      return GuideInsight.emptyMap();
    }
  }));
  public chartData$ = combineLatest([
    this.dataAs,
    this.guideInsights,
    this.guidesDomainModel.allGuides$
  ]).pipe(debounceTime(1), map(([dataAs, guideInsights, guides]) => {
    if (!!guideInsights && guideInsights.length > 0 && !!guides) {
      const totalData: ChartData[] = guideInsights.map(insight => {
        switch (dataAs) {
          case ChartDataAs.GuideViews: return insight.translateGuideViewsToChartData();
          case ChartDataAs.WebsiteViews: return insight.translateWebsiteViewsToChartData();
          case ChartDataAs.FacebookViews: return insight.translateFacebookViewsToChartData();
          case ChartDataAs.InstagramViews: return insight.translateInstagramViewsToChartData();
          case ChartDataAs.TwitterViews: return insight.translateTwitterViewsToChartData();
          case ChartDataAs.Aggregation: return [insight.translateToAggregateChartData()];
          case ChartDataAs.Separate: return insight.translateToSeparateLineChartData();
        }
      }).flatMap(data => data);
      return totalData;
    } else {
      return [] as ChartData[];
    }
  }));

  constructor(
    private guidesDomainModel: GuidesDomainModel,
    private insightsDomainModel: InsightsDomainModel,
  ) {
    super();
    this.init();
  }

  init() {
    super.init();
    this.setupLoadingSpinner();
  }

  private setupLoadingSpinner() {
    this.loadingOpts.isLoading = true;
    this.loadingOpts.backgroundColor = '#FCFCFC';
    this.loadingOpts.spinnerColor = '#222222';
    this.loadingOpts.showLoadingText = true;
    this.loadingOpts.loadingText = 'Loading Insights';
    this.loadingOpts.color = '#222222';
  }

  setChartDataAs(x: ChartDataAs) {
    this.dataAs.next(x);
  }

  setGuideId(x: string) {
    this.guideId.next(x);
  }

  changeSelectedClickType(type: GuideClickType) {
    this.selectedClickType.next(type);
    switch (type) {
      case GuideClickType.Guide: this.dataAs.next(ChartDataAs.GuideViews); break;
      case GuideClickType.Facebook: this.dataAs.next(ChartDataAs.FacebookViews); break;
      case GuideClickType.Instagram: this.dataAs.next(ChartDataAs.InstagramViews); break;
      case GuideClickType.Twitter: this.dataAs.next(ChartDataAs.TwitterViews); break;
      case GuideClickType.Website: this.dataAs.next(ChartDataAs.WebsiteViews); break;
    }
  }

  datesChosen(dates: any) {
    this.selectedDateRange.next(new KrugoDateRange(dates.chosenLabel, dates.startDate, dates.endDate));
  }

  ngOnDestroy() {
    this.destroy();
  }

}
